import { LocalStage, EditingStage } from "../types";

export const useStageManagement = (
  stages: LocalStage[],
  setStages: React.Dispatch<React.SetStateAction<LocalStage[]>>,
  editingStage: EditingStage,
  setEditingStage: React.Dispatch<React.SetStateAction<EditingStage>>,
  setStageError: React.Dispatch<React.SetStateAction<string>>,
  getStagesForSection: (sectionId: number) => LocalStage[]
) => {
  // Add stage click handler
  const handleAddStageClick = (sectionId: number) => {
    const newEditingStage = {
      sectionId,
      stageId: null,
      stageNumber: getStagesForSection(sectionId).length + 1,
      title: "",
      isPublic: true,
      followUpGroupIdsJson: "",
    };
    setEditingStage(newEditingStage);
    setStageError("");
  };

  // Save stage handler
  const handleSaveStage = () => {
    if (editingStage.title.trim() && editingStage.sectionId) {
      // Check for duplicate stage numbers in the same section
      const existingStagesInSection = getStagesForSection(
        editingStage.sectionId
      );
      const isDuplicate = existingStagesInSection.some(
        (stage) =>
          stage.stageNumber === editingStage.stageNumber &&
          stage.id !== editingStage.stageId
      );

      if (isDuplicate) {
        setStageError(
          `شماره مرحله ${editingStage.stageNumber} قبلاً در این بخش استفاده شده است`
        );
        return;
      }

      if (editingStage.stageId) {
        // Update existing stage
        setStages((prev) => {
          const updatedStages = prev.map((stage) =>
            stage.id === editingStage.stageId
              ? {
                  ...stage,
                  stageNumber: editingStage.stageNumber,
                  title: editingStage.title.trim(),
                  isPublic: editingStage.isPublic,
                  followUpGroupIdsJson: editingStage.followUpGroupIdsJson,
                }
              : stage
          );
          // Sort stages by stage number within each section
          return updatedStages.sort((a, b) => {
            if (a.sectionId !== b.sectionId) return a.sectionId - b.sectionId;
            return a.stageNumber - b.stageNumber;
          });
        });
      } else {
        // Add new stage
        const stage: LocalStage = {
          id: `${editingStage.sectionId}_${editingStage.stageNumber}`,
          sectionId: editingStage.sectionId,
          stageNumber: editingStage.stageNumber,
          title: editingStage.title.trim(),
          isPublic: editingStage.isPublic,
          followUpGroupIdsJson: editingStage.followUpGroupIdsJson,
        };
        setStages((prev) => {
          const newStages = [...prev, stage];
          // Sort stages by stage number within each section
          return newStages.sort((a, b) => {
            if (a.sectionId !== b.sectionId) return a.sectionId - b.sectionId;
            return a.stageNumber - b.stageNumber;
          });
        });
      }
      setEditingStage({
        sectionId: null,
        stageId: null,
        stageNumber: 1,
        title: "",
        isPublic: true,
        followUpGroupIdsJson: "",
      });
      setStageError("");
    }
  };

  // Edit stage handler
  const handleEditStage = (stage: LocalStage) => {
    setEditingStage({
      sectionId: stage.sectionId,
      stageId: stage.id,
      stageNumber: stage.stageNumber,
      title: stage.title,
      isPublic: stage.isPublic,
      followUpGroupIdsJson: stage.followUpGroupIdsJson,
    });
    setStageError("");
  };

  // Delete stage handler
  const handleDeleteStage = (stageId: string) => {
    setStages((prev) => prev.filter((stage) => stage.id !== stageId));
  };

  // Cancel editing stage
  const handleCancelEditingStage = () => {
    setEditingStage({
      sectionId: null,
      stageId: null,
      stageNumber: 1,
      title: "",
      isPublic: true,
      followUpGroupIdsJson: "",
    });
    setStageError("");
  };

  return {
    handleAddStageClick,
    handleSaveStage,
    handleEditStage,
    handleDeleteStage,
    handleCancelEditingStage,
  };
};
